// RegistrationSection.jsx
import React, { useState } from 'react';
import { useApplication } from './hooks/useApplication';

const RegistrationSection = ({ applicationData, mode, applicationId }) => {
  const { verifyRegistration, downloadFile } = useApplication(applicationId);
  
  const [registrationComment, setRegistrationComment] = useState(applicationData.registrationComment || '');
  const [loading, setLoading] = useState(false);

  const handleVerifyRegistration = async () => {
    try {
      setLoading(true);
      await verifyRegistration(registrationComment);
    } catch (error) {
      console.error('Error verifying registration:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (document, part) => {
    try {
      await downloadFile(applicationData.id, document.id, part, document.originalName);
    } catch (error) {
      console.error('Error downloading document:', error);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      verified: { class: 'status-verified', label: 'Підтверджено' },
      not_verified: { class: 'status-pending', label: 'Не підтверджено' }
    };
    
    const config = statusConfig[status] || statusConfig.not_verified;
    return <span className={`status-badge ${config.class}`}>{config.label}</span>;
  };

  const getDocumentIcon = (type) => {
    const icons = {
      pdf: '📄',
      jpg: '🖼️',
      png: '🖼️',
      zip: '📦',
      docx: '📝',
      xlsx: '📊'
    };
    return icons[type] || '📎';
  };

  const getRegistrationStatusText = () => {
    return applicationData.registrationStatus === 'verified' 
      ? 'Реєстрація підтверджена в РТГК' 
      : 'Реєстрація не підтверджена в РТГК';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('uk-UA');
    } catch (error) {
      return dateString;
    }
  };

  return (
    <div className="registration-section">
      <h3 className="section-title">Місце реєстрації</h3>
      
      <div className="data-section">
        <div className="data-row">
          <label className="data-label">Зареєстрована адреса проживання:</label>
          <span className="data-value">{applicationData.registeredAddress}</span>
        </div>
        
        <div className="data-row">
          <label className="data-label">Фактична адреса проживання:</label>
          <span className="data-value">{applicationData.actualAddress}</span>
        </div>
        
        <div className="data-row">
          <label className="data-label">Свідоцтво про народження:</label>
          <span className="data-value">{applicationData.birthCertificate}</span>
        </div>
        
        <div className="data-row">
          <label className="data-label">Статус РТГК:</label>
          <div className="status-section">
            {getStatusBadge(applicationData.registrationStatus)}
            <span className="status-text">{getRegistrationStatusText()}</span>
          </div>
        </div>
      </div>

      {/* Документы для регистрации */}
      <div className="documents-section">
        <h4 className="documents-title">Документи для реєстрації</h4>
        <div className="documents-list">
          {applicationData.registrationDocuments?.map(doc => (
            <div key={doc.id} className="document-item">
              <div className="document-info">
                <span className="document-icon">{getDocumentIcon(doc.type)}</span>
                <div className="document-details">
                  <div className="document-name">{doc.name}</div>
                  <div className="document-meta">
                    <span className="document-type">{doc.type}</span>
                    <span className="document-size">{doc.size}</span>
                    <span className="document-date">{doc.uploadedAt}</span>
                  </div>
                </div>
              </div>
              <button 
                className="btn-download"
                onClick={() => handleDownload(doc, "cert")}
                title="Скачати документ"
                disabled={loading}
              >
                ⬇️
              </button>
            </div>
          ))}
          {(!applicationData.registrationDocuments || applicationData.registrationDocuments.length === 0) && (
            <div className="no-documents">Документи відсутні</div>
          )}
        </div>
      </div>

      {/* Комментарий и действия для регистрации */}
      {mode === 'edit' && (
        <div className="actions-section">
          <div className="comment-field">
            <label htmlFor="registration-comment" className="comment-label">
              Коментар до реєстрації:
            </label>
            <textarea
              id="registration-comment"
              className="form-control comment-textarea"
              value={registrationComment}
              onChange={(e) => setRegistrationComment(e.target.value)}
              placeholder="Введіть коментар до реєстрації..."
              rows="3"
            />
          </div>
          
          <div className="action-buttons">
            <button 
              className="btn btn-success"
              onClick={handleVerifyRegistration}
              disabled={applicationData.registrationStatus === 'verified' || loading}
            >
              {loading ? 'Обробка...' : 'Підтвердити в ручному режимі'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegistrationSection;